import { FieldError } from "client/lib/apt-merchant";
import { exactLength, maxLength, minLength } from "utils/validator";

import { Field } from "../../field";
import { Merchant } from "../../merchant";
import { I18nValues, MerchantField, State } from "../../types";

const requiredProvinces: State[] = [
  "Alberta",
  "British Columbia",
  "New Brunswick",
  "Nova Scotia",
  "Ontario",
  "Quebec",
];

const provinceToPlaceholder: { [key in State]?: string } = {
  Alberta: "123456789",
  "British Columbia": "AA1234567",
  "New Brunswick": "123456",
  "Nova Scotia": "1234567",
  Ontario: "1234567",
  Quebec: "1234567890",
};

export const ERRORS = {
  alberta: "Must be between 8-10 alphanumeric character Registry ID",
  britishColumbia: "Must be 7, 8 or 9 alphanumeric character Registry ID.",
  newBrunswick: "Must be 5 or 6 digit Registry ID",
  novaScotia: "Must be 7 digit Registry ID",
  ontario: "Must be 7, 8 or 10 digit Registry ID",
  quebec: "Must be 7, 8, or 10 digit Registry ID",
};

// either province specific placeholder or default
export const getCraRegistryIdPlaceholderValue = (province: State) =>
  provinceToPlaceholder[province] ?? "1234...";

export const getCraRegistryIdErrorMsg = (
  province: State,
  value: string
): string | null => {
  let errorMessage = null;
  const trimmedValue = value.replace(/\W/g, "");

  if (province === "British Columbia") {
    if (!minLength(7)(trimmedValue) || !maxLength(9)(trimmedValue)) {
      errorMessage = ERRORS.britishColumbia;
    }
  } else if (province === "New Brunswick") {
    if (!exactLength(5)(trimmedValue) && !exactLength(6)(trimmedValue)) {
      errorMessage = ERRORS.newBrunswick;
    }
  } else if (province === "Ontario") {
    if (
      !exactLength(7)(trimmedValue) &&
      !exactLength(8)(trimmedValue) &&
      !exactLength(10)(trimmedValue)
    ) {
      errorMessage = ERRORS.ontario;
    }
  } else if (province === "Quebec") {
    if (
      !exactLength(7)(trimmedValue) &&
      !exactLength(8)(trimmedValue) &&
      !exactLength(10)(trimmedValue)
    ) {
      errorMessage = ERRORS.quebec;
    }
  } else if (province === "Nova Scotia") {
    if (!exactLength(7)(trimmedValue)) {
      errorMessage = ERRORS.novaScotia;
    }
  } else if (province === "Alberta") {
    if (!minLength(8)(trimmedValue) || !maxLength(10)(trimmedValue)) {
      errorMessage = ERRORS.alberta;
    }
  }

  return errorMessage;
};

const getLabel = (province: State, label: string) =>
  requiredProvinces.includes(province) ? label : `${label} (optional)`;

export class CraRegistryIdField extends Field<string> {
  constructor(model: Merchant, opts: Partial<MerchantField<string>> = {}) {
    super(model, {
      dataType: "string",
      testValue: "1234567",
      propertyName: "craRegistryId",
      uiFieldType: "Input",
      ...opts,
    });
  }

  get applicable(): boolean {
    const registeredCountry = this._model.companyInformation.registeredCountry
      .value;
    const businessType = this._model.companyInformation.businessType.value;

    if (
      registeredCountry === "Canada" &&
      businessType !== "Sole Proprietorship"
    ) {
      return true;
    }
    return false;
  }

  get shown(): boolean {
    return this.applicable;
  }

  get required(): boolean {
    const province = this._model.companyInformation.businessState
      .value as State;
    const businessType = this._model.companyInformation.businessType.value;
    return (
      this.applicable &&
      businessType === "Corporation" &&
      requiredProvinces.includes(province)
    );
  }

  get i18nValues(): I18nValues {
    const province = this._model.companyInformation.businessState
      .value as State;
    const label = "Corporation Number or Registration Number";
    return {
      label: getLabel(province, label),
      placeholder: getCraRegistryIdPlaceholderValue(province),
    };
  }

  _validate(): void {
    const province = this._model.companyInformation.businessState
      .value as State;
    if (province && this.value) {
      const errMsg = getCraRegistryIdErrorMsg(province, this.value);
      if (errMsg) {
        throw new FieldError(this, errMsg, "InvalidLength");
      }
    }
  }
}
